<template>
  <div class="row">
    <div class="col-md-6" v-if="user || isLoadingUser">
      <loading-card v-if="isLoadingUser" title="Profile" />
      <card title="Profile" v-else-if="user">
        <div class="profile">
          <profile-row heading="Name" :info="user.full_name" />
          <profile-row heading="Email" :info="user.email" />
          <profile-row heading="Organization" v-if="user.organization" :info="user.organization.name" />
          <profile-row heading="Group" v-if="user.group" :info="user.group.name" />
          <profile-row heading="Role" v-if="user.role" :info="user.role.name" />
        </div>
      </card>
    </div>

    <div class="col-md-6">
      <card class="card" title="Change Password">
        <form @submit.prevent>
          <div class="row">
            <div class="col-12">
              <input name="username" type="text" autocomplete="username" readonly style="display: none;" />
              <fg-input type="password" placeholder="Password" autocomplete="new-password"
                :class="{ 'form-group--error': $v.password.$error }" v-model="$v.password.$model"
                :addonRightIcon="passwordIcon" @clickedIcon="togglePasswordIcon" id="password">
              </fg-input>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <fg-input type="password" placeholder="Confirm Password" autocomplete="new-password"
                :class="{ 'form-group--error': $v.confirmPassword.$error }" v-model="$v.confirmPassword.$model"
                :addonRightIcon="confirmPasswordIcon" @clickedIcon="toggleConfirmPasswordIcon" id="confirm-password">
              </fg-input>
            </div>
          </div>
          <div class="text-left">
            <p-button type="info" :loading="isChangingPassword" @click.native.prevent="changePassword">
              Save
            </p-button>
          </div>
        </form>
      </card>

      <card class="card" title="Application API Key">
        <form @submit.prevent>
          <div class="row" v-if="apiKey">
            <div class="col-12">
              <h6>Your new API key was created and <strong>will be displayed only once!</strong></h6>
              <div class="input-group input-group-lg mb-3">
                <input id="api-key" type="text" class="form-control" readonly :value="apiKey">
                <button class="btn" @click="copyKey">COPY</button>
              </div>
              <p>Copy the key now and store it securely. The key cannot be viewed once you navigate away from this page.
              </p>
            </div>
          </div>
          <div class="row" v-else>
            <div class="col-12" v-if="user">
              <p v-if="user.has_api_key">An API key is active</p>
              <div class="d-flex">
                <p-button type="danger" class="mr-2" :loading="isChangingPassword" :disabled="!user.has_api_key"
                  @click.native.prevent="removeApiKey">
                  Remove
                </p-button>
                <p-button type="info" :loading="isChangingPassword" @click.native.prevent="resetApiKey">
                  {{ user.has_api_key ? 'Reset' : 'Set' }}
                </p-button>
              </div>
            </div>
          </div>
        </form>
      </card>
    </div>

    <div class="col-md-6" v-if="canChangeServiceNowConnector">
      <card class="card" title="ServiceNow Connector">
        <form @submit.prevent>
          <div class="row">
            <div class="col-12">
              <label for="service-now-instance-name">Instance Name</label>
              <fg-input type="text" placeholder="Instance Name"
                :class="{ 'form-group--error': $v.serviceNowInstanceName.$error }"
                v-model="$v.serviceNowInstanceName.$model" id="service-now-instance-name">
              </fg-input>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <label for="service-now-client-id">Client ID</label>
              <fg-input type="text" placeholder="Client ID" autocomplete="username"
                :class="{ 'form-group--error': $v.serviceNowClientId.$error }" v-model="$v.serviceNowClientId.$model"
                id="service-now-client-id">
              </fg-input>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <label for="service-now-client-secret">Client Secret</label>
              <fg-input type="password" placeholder="Client Secret" autocomplete="current-password"
                :class="{ 'form-group--error': $v.serviceNowClientSecret.$error }"
                v-model="$v.serviceNowClientSecret.$model"
                :addonRightIcon="serviceNowConnector ? null : serviceNowClientSecretIcon"
                @clickedIcon="toggleClientSecretVisible" id="service-now-client-secret">
              </fg-input>
            </div>
          </div>
          <div class="d-flex">
            <p-button type="danger" class="mr-2" :loading="isUpdatingServiceNowConnector"
              :disabled="serviceNowConnector === null" @click.native.prevent="deleteServiceNowConnector">
              Delete
            </p-button>
            <p-button type="info" :loading="isUpdatingServiceNowConnector" :disabled="!serviceNowDirty"
              @click.native.prevent="updateServiceNowConnector">
              Save
            </p-button>
            <p :class="serviceNowMessageClass">{{ serviceNowMessage }}</p>
          </div>
        </form>
      </card>
    </div>

  </div>
</template>

<script>
import ProfileRow from '../components/ProfileRow.vue';
import Card from '../components/Cards/Card.vue';
import LoadingCard from '../components/Cards/LoadingCard.vue';
import { required, sameAs } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import { notify } from '../services/notify';

export default {
  components: {
    ProfileRow,
    Card,
    LoadingCard
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      passwordIcon: 'eye-slash',
      confirmPasswordIcon: 'eye-slash',
      serviceNowInstanceName: '',
      serviceNowClientId: '',
      serviceNowClientSecret: '',
      serviceNowClientSecretIcon: 'eye-slash',
      serviceNowMessage: '',
      serviceNowMessageClass: 'service-now-message-success',
      isLoadingUser: false,
      isChangingPassword: false,
      isChangingApiKey: false,
      isUpdatingServiceNowConnector: false,
    }
  },
  validations: {
    password: { required },
    confirmPassword: { sameAsPassword: sameAs('password') },
    serviceNowInstanceName: { required },
    serviceNowClientId: { required },
    serviceNowClientSecret: { required },
  },
  created() {
    if (!this.user) {
      this.fetchMe();
    }
    if (this.canChangeServiceNowConnector) {
      this.fetchServiceNowConnector();
    }
  },
  mounted() {
    if (this.canChangeServiceNowConnector) {
      this.fetchServiceNowConnector();
    }
  },
  async beforeDestroy() {
    await this.$store.dispatch('user/clearApiKey');
  },
  methods: {
    async fetchMe() {
      this.isLoadingUser = true;
      await this.$store.cache.dispatch('user/fetchMe');
      this.isLoadingUser = false;
    },
    async changePassword() {
      this.$v.password.$touch();
      this.$v.confirmPassword.$touch();
      if (this.$v.$invalid) {
        notify().error('The two passwords must match.')
        return;
      }

      this.isChangingPassword = true;
      const success = await this.$store.dispatch('user/updateMe', {
        password: this.password
      });
      this.isChangingPassword = false;

      if (success) {
        this.resetPasswordForm();
      }
    },
    resetPasswordForm() {
      this.password = "";
      this.confirmPassword = "";
      this.$v.$reset();
    },
    async resetApiKey() {
      this.isChangingApiKey = true;
      const success = await this.$store.dispatch('user/createApiKey');
      this.isChangingApiKey = false;
    },
    async removeApiKey() {
      this.isChangingApiKey = true;
      const success = await this.$store.dispatch('user/deleteApiKey');
      this.isChangingApiKey = false;
    },
    copyKey() {
      let copyInput = document.getElementById("api-key");
      copyInput.select();
      copyInput.setSelectionRange(0, 99999);

      document.execCommand("copy");
      notify().success('Success! Copied API Key to clipboard.');
      copyInput.blur();
    },
    async fetchServiceNowConnector() {
      if (!this.serviceNowConnector) {
        this.isUpdatingServiceNowConnector = true;
        await this.$store.cache.dispatch('organization/fetchServiceNowConnector');
        this.isUpdatingServiceNowConnector = false;
      }
      if (this.serviceNowConnector) {
        this.serviceNowInstanceName = this.serviceNowConnector.instance_name;
        this.serviceNowClientId = this.serviceNowConnector.client_id;
        this.serviceNowClientSecret = this.serviceNowConnector.client_secret;
      }
    },
    async updateServiceNowConnector() {
      this.$v.serviceNowInstanceName.$touch();
      this.$v.serviceNowClientId.$touch();
      this.$v.serviceNowClientSecret.$touch();
      if (this.$v.serviceNowInstanceName.$invalid) {
        notify().error('Enter the instance name.')
        return;
      }
      if (this.$v.serviceNowClientId.$invalid) {
        notify().error('Enter the client ID.')
        return;
      }
      if (this.$v.serviceNowClientSecret.$invalid) {
        notify().error('Enter the client secret.')
        return;
      }

      this.isUpdatingServiceNowConnector = true;
      this.serviceNowMessage = 'Checking the connection to ServiceNow...';
      this.serviceNowMessageClass = 'service-now-message-success';
      let message = await this.$store.dispatch('organization/updateServiceNowConnector', {
        instance_name: this.serviceNowInstanceName,
        client_id: this.serviceNowClientId,
        client_secret: this.serviceNowClientSecret,
      });
      this.isUpdatingServiceNowConnector = false;
      if (message !== null) {
        this.serviceNowMessage = message;
        this.serviceNowMessageClass = 'service-now-message-error';
      }
      else {
        this.serviceNowMessage = 'Credentials verified!';
        this.serviceNowMessageClass = 'service-now-message-success';
        this.serviceNowInstanceName = this.serviceNowConnector.instance_name;
        this.serviceNowClientId = this.serviceNowConnector.client_id;
        this.serviceNowClientSecret = this.serviceNowConnector.client_secret;
      }
    },
    async deleteServiceNowConnector() {
      window.confirm('This action will disconnect the Service Now Connector. Are you sure you want to continue?');
      this.isUpdatingServiceNowConnector = true;
      await this.$store.dispatch('organization/deleteServiceNowConnector');
      this.serviceNowInstanceName = "";
      this.serviceNowClientId = "";
      this.serviceNowClientSecret = "";
      this.$v.serviceNowInstanceName.$reset();
      this.$v.serviceNowClientId.$reset();
      this.$v.serviceNowClientSecret.$reset();
      this.isUpdatingServiceNowConnector = false;
    },
    toggleClientSecretVisible() {
      this.toggleEye('service-now-client-secret', this.serviceNowClientSecretIcon);
    },
    togglePasswordIcon() {
      this.toggleEye('password', this.passwordIcon);
    },
    toggleConfirmPasswordIcon() {
      this.toggleEye('confirm-password', this.confirmPasswordIcon);
    },
    toggleEye(id, v) {
      let input = document.getElementById(id)
      if (input.type === 'password') {
        input.type = 'text';
        v = 'eye';
      }
      else {
        input.type = 'password';
        v = 'eye-slash';
      }
    },
  },
  computed: {
    ...mapGetters('user', ['user', 'apiKey']),
    ...mapGetters('organization', ['serviceNowConnector']),
    canChangeApiKey: function () {
      if (!this.user) return false;
      let role = this.user.role.id;
      return (role === 'super' || role === 'staff' || role === 'partner' || role === 'manager');
    },
    canChangeServiceNowConnector: function () {
      if (!this.user) return false;
      let role = this.user.role.id;
      return (role === 'super' || role === 'staff' || role === 'partner' || role === 'manager');
    },
    serviceNowDirty: function () {
      if (this.serviceNowConnector === null) return true;
      return !(
        this.serviceNowConnector.instance_name === this.serviceNowInstanceName
        && this.serviceNowConnector.client_id === this.serviceNowClientId
        && this.serviceNowConnector.client_secret === this.serviceNowClientSecret
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.profile {
  padding-left: 10px;
}

.service-now-message-error {
  padding-left: 20px;
  font-size: 16px;
  color: darkred;
}

.service-now-message-success {
  padding-left: 20px;
  font-size: 16px;
  color: darkgreen;
}
</style>
